import React, { useState, useEffect } from 'react';
import { db } from '../Firebase';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { 
  Mail, 
  Phone, 
  MessageSquare, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  User,
  Search,
  Filter,
  ArrowRight,
  MoreVertical,
  Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';

const ContactInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    const q = query(collection(db, 'contact_inquiries'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setInquiries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleFollowUp = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'contact_inquiries', id), {
        followUpDone: !currentStatus,
        updatedAt: new Date()
      });
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Marked as ${!currentStatus ? 'Completed' : 'Pending'}`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'contact_inquiries', id));
        Swal.fire(
          'Deleted!',
          'Inquiry has been deleted.',
          'success'
        );
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Failed to delete inquiry', 'error');
      }
    }
  };

  const filteredInquiries = inquiries.filter(inq => {
    const matchesSearch = inq.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          inq.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          inq.phone?.includes(searchTerm);
    const matchesStatus = filterStatus === 'All' || 
                          (filterStatus === 'Done' && inq.followUpDone) || 
                          (filterStatus === 'Pending' && !inq.followUpDone);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Contact Inquiries</h1>
          <p className="text-gray-500 font-medium">Manage general messages and customer support requests.</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm">
            <Filter className="w-4 h-4 text-primary" />
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-transparent border-none outline-none text-sm font-bold text-gray-700"
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Done">Followed Up</option>
            </select>
          </div>

          <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-gray-100 shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Search className="w-5 h-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search contacts..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm text-gray-700 w-48"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Inquiry Details</th>
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Inquiry Type</th>
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Message</th>
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                <th className="px-8 py-6 text-xs font-bold text-gray-400 uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <AnimatePresence>
                {filteredInquiries.length > 0 ? (
                  filteredInquiries.map((inq, index) => (
                    <motion.tr
                      key={inq.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50/50 transition-colors group"
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary/5 text-primary rounded-xl flex items-center justify-center font-bold">
                            {inq.name?.[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-primary-dark">{inq.name}</p>
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                              <Mail className="w-3 h-3" /> {inq.email}
                            </p>
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {inq.phone}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-primary/5 text-primary border border-primary/10">
                          {inq.inquiryType || 'General Inquiry'}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-sm text-gray-600 line-clamp-2 max-w-xs italic leading-relaxed">
                          "{inq.message}"
                        </p>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
                          <Calendar className="w-4 h-4 text-secondary" />
                          {inq.createdAt?.toDate().toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                          inq.followUpDone 
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                          : 'bg-amber-50 text-amber-600 border border-amber-100'
                        }`}>
                          {inq.followUpDone ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {inq.followUpDone ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFollowUp(inq.id, inq.followUpDone)}
                            className={`p-2.5 rounded-xl border transition-all ${
                              inq.followUpDone 
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 shadow-sm' 
                              : 'bg-white text-primary border-primary/20 hover:bg-primary hover:text-white shadow-sm'
                            }`}
                            title={inq.followUpDone ? "Mark as Pending" : "Mark as Completed"}
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(inq.id)}
                            className="p-2.5 rounded-xl border bg-red-50 text-red-600 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-8 py-20 text-center">
                      <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-10 h-10 text-gray-300" />
                      </div>
                      <h3 className="text-xl font-bold text-primary-dark">No inquiries found</h3>
                      <p className="text-gray-500">All caught up or try a different filter.</p>
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactInquiries;
